// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import InnerTable from "lib/Tables/InnerTable";
import TransactionsFilterToolbar from "../../bankStatements/TransactionTableToolbar";
import MDDatePicker from "components/MDDatePicker";
import DefaultCell from "layouts/ecommerce/orders/order-list/components/DefaultCell";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { Autocomplete, Box, Checkbox, Chip, createTheme, Grid, TextField } from "@mui/material";
import { post } from "requests";
import MDSelect from "components/MDSelect";
import OfferCustomSelect from "pages/offers/OfferCustomSelect";
import CustomModal from "components/CustomModal";
import { Switch } from "@mui/material";
import { ThemeProvider } from "@emotion/react";
import {
  AttachMoney,
  CheckBox,
  CheckBoxOutlineBlank,
  Clear,
  Description,
  ExpandMore,
  FiberManualRecord,
  LocalOffer,
} from "@mui/icons-material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

export default function OcrolusTransactions({
  transactions = [],
  availableTags = [],
  bankAccounts = {},
  loading = false,
  dealApplicationId,
  setTransactions,
  onRefresh,
}) {
  const [expandedTags, setExpandedTags] = useState({});
  const [tagFilters, setTagFilters] = useState([]);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const REVENUE_TAG_NAME = "revenue";
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");


  const [filters, setFilters] = useState<{ debit: boolean; credit: boolean }>({
    debit: true,
    credit: true,
  });

  const [dateFilters, setDateFilters] = useState({
    fromDate: null,
    toDate: null,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    
    return () => clearTimeout(handler);
  }, [searchInput]);

  
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);
        if (value.trim() === "") {
      setSearchQuery("");
    }
  }, []);
  

  const [editingTxn, setEditingTxn] = useState(null);

  const tagByName = useMemo(() => {
    const map = {};
    availableTags.forEach((t) => (map[t.name] = t));
    return map;
  }, [availableTags]);

  const extractTags = (txn) => availableTags.filter((tag) => txn[tag.name] === true);

  const hasRevenueTag = (txn) => {
    return (txn.tags || []).some((t) => t.name?.toLowerCase() === REVENUE_TAG_NAME);
  };

  const toggleExpandTags = (txnPk) => {
    setExpandedTags((prev) => ({
      ...prev,
      [txnPk]: !prev[txnPk],
    }));
  };

  const openTagEditor = (txn) => {
    setEditingTxn(txn);
    setSelectedTags(txn.tags || []);
    setIsTagModalOpen(true);
  };

  const closeTagEditor = () => {
    setIsTagModalOpen(false);
    setEditingTxn(null);
    setSelectedTags([]);
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setFilters({ debit: true, credit: true });
    setDateFilters({ fromDate: null, toDate: null });
    setTagFilters([]);
  };;

  const buildTxnTagPayload = (txn, nextTags) => {
    const originalTags = txn.tags || [];
    const originalUuids = new Set(originalTags.map((t) => t.uuid));
    const nextUuids = new Set(nextTags.map((t) => t.uuid));

    const tagMap = new Map();

    originalTags.forEach((tag) => {
      tagMap.set(tag.uuid, {
        tag_uuid: tag.uuid,
        apply_to_txn: nextUuids.has(tag.uuid),
      });
    });

    nextTags.forEach((tag) => {
      if (!originalUuids.has(tag.uuid)) {
        tagMap.set(tag.uuid, {
          tag_uuid: tag.uuid,
          apply_to_txn: true,
        });
      }
    });

    return {
      txns: [
        {
          txn_pk: txn.txnPk,
          tags: Array.from(tagMap.values()),
        },
      ],
    };
  };

  const submitTags = async () => {
    if (!editingTxn) return;
  
    const previousTxn = editingTxn;

      const nextBooleans = {};
    availableTags.forEach((tag) => {
      nextBooleans[tag.name] = selectedTags.some(
        (t) => t.uuid === tag.uuid
      );
    });
  
    setTransactions((prev) =>
      prev.map((t) =>
        t.txn_pk === editingTxn.txnPk
          ? { ...t, ...nextBooleans }
          : t
      )
    );
  
    closeTagEditor();
  
    const payload = buildFullTxnTagPayload(editingTxn, selectedTags);
  
    try {
      await post(
        `/integration/ocrolous/transactions/${dealApplicationId}/tags`,
        payload
      );
      onRefresh?.();
    } catch {
      setTransactions((prev) =>
        prev.map((t) =>
          t.txn_pk === editingTxn.txnPk ? previousTxn : t
        )
      );
    }
  };
  
  
  const handleIncomeToggle = async (txn, checked) => {
    const revenueTag = availableTags.find(
      (t) => t.name?.toLowerCase() === REVENUE_TAG_NAME
    );
    if (!revenueTag) return;
  
    const previousValue = txn[revenueTag.name];
  
    setTransactions((prev) =>
      prev.map((t) =>
        t.txn_pk === txn.txnPk
          ? { ...t, [revenueTag.name]: checked }
          : t
      )
    );
  
    const payload = buildFullTxnTagPayload(
      txn,
      checked
        ? [...(txn.tags || []), revenueTag]
        : (txn.tags || []).filter((t) => t.uuid !== revenueTag.uuid)
    );
  
    try {
      await post(
        `/integration/ocrolous/transactions/${dealApplicationId}/tags`,
        payload
      );
      onRefresh?.();
    } catch {
      setTransactions((prev) =>
        prev.map((t) =>
          t.txn_pk === txn.txnPk
            ? { ...t, [revenueTag.name]: previousValue }
            : t
        )
      );
    }
  }

  const buildFullTxnTagPayload = (txn, selectedTags) => {
    const selectedUuids = new Set(selectedTags.map((t) => t.uuid));
    const existingUuids = new Set((txn.tags || []).map((t) => t.uuid));
  
    const payloadTags = [];
  
    (txn.tags || []).forEach((tag) => {
      payloadTags.push({
        tag_uuid: tag.uuid,
        apply_to_txn: selectedUuids.has(tag.uuid),
      });
    });
  
    selectedTags.forEach((tag) => {
      if (!existingUuids.has(tag.uuid)) {
        payloadTags.push({
          tag_uuid: tag.uuid,
          apply_to_txn: true,
        });
      }
    });
  
    return {
      txns: [
        {
          txn_pk: txn.txnPk,
          tags: payloadTags,
        },
      ],
    };
  };

  const incomeSwitchTheme = createTheme({
    palette: {
      primary: { main: "#2F929F" },
    },
  });

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>, filterType: string) => {
    const newFilters = { ...filters, [filterType]: event.target.checked };
    setFilters(newFilters);
  };

  const formatTagName = (name = "") =>
    name
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const getReadableTextColor = (bgColor = "#E0E0E0") => {
    const hex = bgColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 160 ? "#000000" : "#FFFFFF";
  };

  const getDisplayTagName = (tag) => {
    if (!tag?.name) return "";

    if (tag.name.toLowerCase() === "revenue") {
      return "Income";
    }

    return formatTagName(tag.name);
  };
    
  const rows = useMemo(() => {
    return transactions.map((txn) => {
      const bank = bankAccounts[txn.bank_account_pk] || {};
  
      const baseRow = {
        txnPk: txn.txn_pk,
        txnDate: txn.txn_date,
        type: txn.amount >= 0 ? "credit" : "debit",
        description: txn.description,
        memo: txn.comment,
        accountName: bank.bank_name || "-",
        accountNumber: bank.account_number || "-",
        amount: txn.amount,
        tags: extractTags(txn),
      };
  
      return {
        ...baseRow,
        _searchBlob: JSON.stringify(baseRow).toLowerCase(),
      };
    });
  }, [transactions, bankAccounts, availableTags]);

  const handleDateChange = (key, value) => {
    setDateFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "txnDate",
        minWidth: 90,
        Cell: ({ value }) => <DefaultCell value={value} />,
      },
      {
        Header: "Type",
        accessor: "type",
        minWidth: 80,
        Cell: ({ value }) => (
          <DefaultCell value={value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"} />
        ),
      },
      {
        Header: "Description",
        accessor: "description",
        minWidth: 220,
        Cell: ({ value }) => value,
      },
      {
        Header: "Memo",
        accessor: "memo",
        minWidth: 180,
        Cell: ({ value }) => <DefaultCell value={value || "-"} />,
      },
      {
        Header: "Bank Name",
        accessor: "accountName",
        minWidth: 160,
        Cell: ({ value }) => <DefaultCell value={value || "-"} />,
      },
      {
        Header: "Account #",
        accessor: "accountNumber",
        minWidth: 120,
        Cell: ({ value }) => <DefaultCell value={value || "-"} />,
      },
      {
        Header: "Amount",
        accessor: "amount",
        minWidth: 110,
        Cell: ({ value }) => <DefaultCell value={formatCurrencyUSD(value)} />,
      },
      {
        Header: "Income / True",
        accessor: "income",
        minWidth: 90,
        Cell: ({ row }) => {
          const txn = row.original;
          const isIncome = hasRevenueTag(txn);

          return (
            <ThemeProvider
              theme={incomeSwitchTheme}
            >
              <Switch
                size="medium"
                color="primary"
                checked={isIncome}
                onChange={(e) => handleIncomeToggle(txn, e.target.checked)}
              />
            </ThemeProvider>
          );
        },
      },
      {
        Header: "Tags",
        accessor: "tags",
        minWidth: 260,
        maxWidth: 300,
        Cell: ({ row }) => {
          const tags = row.original.tags || [];
          const txnPk = row.original.txnPk;
          const isExpanded = expandedTags[txnPk];

          const visibleTags = isExpanded ? tags : tags.slice(0, 2);
          const remainingCount = tags.length - visibleTags.length;

          return (
            <MDBox
              sx={{
                width: "100%",
                minWidth: "260px",
                maxWidth: "300px",
                boxSizing: "border-box",
              }}
            >
              <MDBox
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  display: "block",
                  boxSizing: "border-box",
                }}
              >
                <MDBox
                  display="flex"
                  flexWrap={isExpanded ? "wrap" : "nowrap"}
                  alignItems="flex-start"
                  gap={0.5}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: isExpanded ? "visible" : "hidden",
                    flexShrink: 1,
                    flexBasis: "auto",
                    boxSizing: "border-box",
                  }}
                >
                  {visibleTags.length ? (
                    visibleTags.map((tag) => {
                      const bg = tag.color || "#E0E0E0";
                      const textColor = getReadableTextColor(bg);

                      return (
                        <Chip
                          key={tag.uuid}
                          label={getDisplayTagName(tag)}
                          size="small"
                          onClick={() => openTagEditor(row.original)}
                          title={tag.description}
                          sx={{
                            backgroundColor: bg,
                            color: textColor,
                            fontSize: "12px",
                            fontWeight: 400,
                            height: 22,
                            cursor: "pointer",
                            flexShrink: 0,
                            flexBasis: "auto",
                            whiteSpace: "nowrap",
                          }}
                        />
                      );
                    })
                  ) : (
                    <DefaultCell value="-" />
                  )}

                  {!isExpanded && remainingCount > 0 && (
                    <Chip
                      label={`+${remainingCount} more`}
                      size="small"
                      clickable
                      onClick={() => openTagEditor(row.original)}
                      sx={{
                        height: 22,
                        fontSize: "12px",
                        border: "1px dashed #90CAF9",
                        backgroundColor: "transparent",
                        color: "#1976d2",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    />
                  )}

                  {isExpanded && tags.length > 2 && (
                    <Chip
                      label="Show less"
                      size="small"
                      clickable
                      onClick={() => openTagEditor(row.original)}
                      sx={{
                        height: 22,
                        fontSize: "12px",
                        border: "1px dashed #90CAF9",
                        backgroundColor: "transparent",
                        color: "#1976d2",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </MDBox>
              </MDBox>
            </MDBox>
          );
        },
      },
    ],
    [
      expandedTags,
      availableTags,
      bankAccounts,
      handleIncomeToggle,
    ]
  )

  const filteredRows = useMemo(() => {
    const query = searchQuery?.trim().toLowerCase();
    return rows.filter((row) => {
      const txnDate = new Date(row.txnDate);

      if (dateFilters.fromDate && txnDate < new Date(dateFilters.fromDate)) return false;
      if (dateFilters.toDate && txnDate > new Date(dateFilters.toDate)) return false;

      if (row.type === "debit" && !filters.debit) return false;
      if (row.type === "credit" && !filters.credit) return false;

      if (tagFilters.length > 0) {
        const txnTagUuids = (row.tags || []).map((t) => t.uuid);

        const hasMatch = tagFilters.some((selected) => txnTagUuids.includes(selected.value));

        if (!hasMatch) return false;
      }

      if (query && !row._searchBlob.includes(query)) return false;

      return true;
    });
  }, [rows, filters, dateFilters, searchQuery, tagFilters]);

  const saveTags = async (txnPk, selectedTags) => {
    await post(`integration/ocrolous/transactions/${txnPk}/tags`, {
      tagUuids: selectedTags.map((t) => t.uuid),
    });
    setEditingTxn(null);
  };

  if (loading) return null;

  const tagOptions = availableTags.map((t) => ({
    label: t.name?.toLowerCase() === "revenue" ? "Income" : formatTagName(t.name),
    value: t.uuid,
    color: t.color || "#E0E0E0",
  }));

  return (
    <MDBox mt={1} p={1} sx={{ backgroundColor: "#ffffff", borderRadius: 2 }}>
       <MDTypography variant="h5" fontWeight="bold" paddingLeft={2}>
            Transactions
          </MDTypography>
      <MDBox sx={{ overflowY: "auto" }}>
        <InnerTable
          table={{ columns, rows: filteredRows }}
          scrollHeight="50vh"
          entriesPerPage={{
            defaultValue: 100,
            entries: ["100", "150", "200", "250"],
          }}
          enableMultiFormatExport={false}
          paginationEnabled
          hasHeaders
          hasFilterComponent
          enableScroll={true}
          filterComponent={
            <TransactionsFilterToolbar
              onFilterChange={handleFilterChange}
              filters={filters}
              onClearFilters={resetFilters}
            />
          }
          dateFilter={
            <MDBox display="flex" justifyContent="flex-end" gap={2}>
              <MDInput
                placeholder="Search"
                value={searchInput}
                onChange={handleSearchChange}
                sx={{
                  width: 200,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.875rem",
                  },
                  "& input": {
                    padding: "6px 10px",
                  },
                }}
              />

              <OfferCustomSelect
                label="Filter by Tags"
                options={tagOptions}
                multiple
                width={300}
                value={tagFilters}
                filterOptions={true}
                onChange={(value) => setTagFilters(value || [])}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderTags={(value, getTagProps) => {
                  if (value.length <= 1) {
                    return value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.value}
                        label={option.label}
                        size="small"
                        sx={{
                          backgroundColor: `${option.color} !important `,
                          color: "#fff",
                          fontSize: "12px",
                          height: 22,
                        }}
                      />
                    ));
                  }

                  return (
                    <Chip
                      label={`${value.length} tags selected`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "12px",
                        backgroundColor: "#E3F2FD",
                        color: "#1976d2",
                      }}
                    />
                  );
                }}
                renderOption={(props, option, { selected }) => {
                  const tagColor = option.color || "#E0E0E0";
                  const textColor = getReadableTextColor(tagColor);
                  const originalTag = availableTags.find((tag) => tag.uuid === option.value);

                  return (
                    <li {...props}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                          py: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: tagColor,
                            flexShrink: 0,
                            border:
                              textColor === "#FFFFFF"
                                ? `1px solid rgba(255,255,255,0.3)`
                                : `1px solid rgba(0,0,0,0.1)`,
                          }}
                        />
                        <Box sx={{ flexGrow: 1 }}>
                          <MDTypography variant="body2" color="text.primary" fontWeight="medium">
                            {option.label}
                          </MDTypography>
                          {originalTag?.description && (
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.25 }}
                            >
                              {originalTag.description}
                            </MDTypography>
                          )}
                        </Box>
                      </Box>
                    </li>
                  );
                }}
              />

              <MDDatePicker
                input={{ placeholder: "From Date" }}
                value={dateFilters.fromDate}
                onChange={(date) => handleDateChange("fromDate", date)}
              />
              <MDDatePicker
                input={{ placeholder: "To Date" }}
                value={dateFilters.toDate}
                onChange={(date) => handleDateChange("toDate", date)}
              />
            </MDBox>
          }
        />
      </MDBox>

      <CustomModal
        open={isTagModalOpen}
        onClose={closeTagEditor}
        onCancel={closeTagEditor}
        onSubmit={submitTags}
        title="Edit Transaction Tags"
        submitText="Save Changes"
        cancelText="Cancel"
        maxWidth="sm"
        fullWidth
      >
        <MDBox
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === "light" ? theme.palette.grey[50] : theme.palette.grey[900],
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <MDTypography variant="h6" color="text.primary" fontWeight="medium" gutterBottom>
            Transaction Details
          </MDTypography>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Description fontSize="small" sx={{ opacity: 0.7, color: "text.secondary" }} />
                <MDTypography
                  variant="body1"
                  color="text.primary"
                  fontWeight="regular"
                  sx={{ wordBreak: "break-word" }}
                >
                  {editingTxn?.description || "No description"}
                </MDTypography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoney
                  fontSize="small"
                  sx={{
                    opacity: 0.9,
                    color: editingTxn?.amount >= 0 ? "success.main" : "error.main",
                  }}
                />
                <MDTypography
                  variant="h5"
                  color={editingTxn?.amount >= 0 ? "success.main" : "error.main"}
                  fontWeight="bold"
                >
                  {formatCurrencyUSD(editingTxn?.amount)}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {editingTxn?.amount >= 0 ? "Credit" : "Debit"}
                </MDTypography>
              </Box>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mb={2}>
          <MDTypography
            variant="subtitle1"
            color="text.primary"
            fontWeight="medium"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <LocalOffer fontSize="small" />
            Transaction Tags
          </MDTypography>

          <MDTypography variant="caption" color="text.secondary" gutterBottom>
            Select one or more tags to categorize this transaction
          </MDTypography>

          <Autocomplete
            multiple
            options={availableTags}
            value={selectedTags}
            getOptionLabel={(tag) => tag.name || tag.label || `Tag ${tag.uuid}`}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            disableCloseOnSelect
            onChange={(_, value) => setSelectedTags(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search or select tags"
                variant="outlined"
                fullWidth
                sx={{
                  mt: 1.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "light"
                        ? theme.palette.common.white
                        : theme.palette.grey[900],
                    minHeight: "56px",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                const tagColor = tag.color || "#E0E0E0";
                const tagName = tag.name || tag.label || `Tag ${tag.uuid}`;
                const readableName = getDisplayTagName(tag);
                const textColor = getReadableTextColor(tagColor);

                return (
                  <Chip
                    key={key}
                    label={readableName}
                    {...tagProps}
                    size="small"
                    title={tag.description}
                    sx={{
                      backgroundColor: `${tagColor} !important`,
                      color: `${textColor} !important`,
                      fontSize: "12px",
                      fontWeight: 400,
                      height: 22,
                      cursor: "pointer",
                      flexShrink: 0,
                      margin: 0.5,
                    }}
                  />
                );
              })
            }
            renderOption={(props, option, { selected }) => {
              const tagColor = option.color || getReadableTextColor(option.color) || "#1976d2";
              const tagName = option.name || option.label || `Tag ${option.uuid}`;
              const readableName = getDisplayTagName(option);

              return (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={
                      <CheckBoxOutlineBlank fontSize="small" sx={{ color: "primary.main" }} />
                    }
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      width: "100%",
                      py: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: tagColor,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <MDTypography variant="body2" color="text.primary" fontWeight="medium">
                        {readableName}
                      </MDTypography>
                      {option.description && (
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.25 }}
                        >
                          {option.description}
                        </MDTypography>
                      )}
                    </Box>
                  </Box>
                </li>
              );
            }}
            sx={{
              "& .MuiAutocomplete-tag": {
                margin: 0.5,
              },
            }}
            ListboxProps={{
              style: {
                maxHeight: "250px",
                borderRadius: "8px",
                padding: "8px 0",
              },
            }}
            popupIcon={<ExpandMore />}
            clearIcon={<Clear />}
          />

          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}
          >
            <MDTypography variant="caption" color="text.secondary">
              {selectedTags.length} tag{selectedTags.length !== 1 ? "s" : ""} selected
            </MDTypography>
          </Box>
        </MDBox>
      </CustomModal>
    </MDBox>
  );
}
